import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import { fetchCepData } from 'src/utils/fetchCepData'

import type { LocalAddressesPayload } from '../domain/addressManagerReconciliation'
import {
  isLocalAddressesArrayPayload,
  resolveFallbackAddressFromFormPayload,
} from '../domain/addressManagerReconciliation'
import { sortDefaultAddressFirst } from '../domain/addressSelection'
import { syncContextAddressFromAddressData } from '../utils/syncContextAddressFromAddressData'

type AddressDraft = Partial<AddressData> | null

interface UseAddressManagerControllerParams {
  addresses: AddressData[]
  defaultAddressId: string | null
  selectedAddressId: string | undefined | null
  onSelectedAddressChange?: (addressId: string | null) => void
  setDefaultAddressId: Dispatch<SetStateAction<string | null>>
  setPostalCode: (postalCode: string) => void
  setAddress: Dispatch<SetStateAction<Address>>
}

export function useAddressManagerController({
  addresses,
  defaultAddressId,
  selectedAddressId,
  onSelectedAddressChange,
  setDefaultAddressId,
  setPostalCode,
  setAddress,
}: UseAddressManagerControllerParams) {
  const [editingAddress, setEditingAddress] = useState<AddressDraft>(null)
  const [addingAddress, setAddingAddress] = useState(false)
  const [newCep, setNewCep] = useState('')
  const [localAddresses, setLocalAddresses] = useState<AddressData[]>(addresses)
  const [localDefaultAddressId, setLocalDefaultAddressId] =
    useState(defaultAddressId)

  const [localSelectedAddressId, setLocalSelectedAddressId] = useState<
    string | undefined | null
  >(selectedAddressId)

  const sortedAddresses = sortDefaultAddressFirst(
    localAddresses,
    localDefaultAddressId,
    localSelectedAddressId
  )

  const addressesCountLabel = `${localAddresses.length} ${
    localAddresses.length === 1 ? 'endereço' : 'endereços'
  }`

  const addressFormModalKey =
    editingAddress?.addressId ?? JSON.stringify(editingAddress) ?? 'new'

  const exitAddressEditor = () => {
    setEditingAddress(null)
    setAddingAddress(false)
    setNewCep('')
  }

  const handleFormSyncLocalAddresses = (payload: LocalAddressesPayload) => {
    if (isLocalAddressesArrayPayload(payload)) {
      setLocalAddresses(payload)

      return
    }

    const { updatedAddresses, selectedAddressId: selectedIdFromPayload } =
      payload

    const fallbackAddress = resolveFallbackAddressFromFormPayload({
      updatedAddresses,
      selectedAddressId: selectedIdFromPayload,
      addingAddress,
      newCep,
    })

    const nextSelectedId = fallbackAddress?.addressId ?? null

    setLocalAddresses(updatedAddresses)
    setLocalSelectedAddressId(nextSelectedId)
    onSelectedAddressChange?.(nextSelectedId)

    if (fallbackAddress) {
      syncContextAddressFromAddressData(
        fallbackAddress,
        setAddress,
        setPostalCode
      )
    }
  }

  const handleCardSyncLocalAddresses = (payload: LocalAddressesPayload) => {
    if (isLocalAddressesArrayPayload(payload)) {
      setLocalAddresses(payload)

      return
    }

    const { updatedAddresses, selectedAddressId: nextSelectedId } = payload

    setLocalAddresses(updatedAddresses)
    setLocalSelectedAddressId(nextSelectedId)
    onSelectedAddressChange?.(nextSelectedId)

    const nextSelectedAddress = updatedAddresses.find(
      (item) => item.addressId === nextSelectedId
    )

    if (nextSelectedAddress) {
      syncContextAddressFromAddressData(
        nextSelectedAddress,
        setAddress,
        setPostalCode
      )
    }
  }

  const handleNewCepSubmit = async (value: string) => {
    const cepData = await fetchCepData(value)

    if (!cepData) return

    setEditingAddress(cepData)
  }

  const startAddingAddress = () => {
    setAddingAddress(true)
    setEditingAddress(null)
  }

  const handleSelectAddress = (address: AddressData) => {
    const nextSelectedAddressId = address?.addressId ?? null

    setLocalSelectedAddressId(nextSelectedAddressId)
    onSelectedAddressChange?.(nextSelectedAddressId)
    syncContextAddressFromAddressData(address, setAddress, setPostalCode)
  }

  const handleMakeDefaultAddress = (addressId: string | null) => {
    setDefaultAddressId(addressId)
    setLocalDefaultAddressId(addressId)
  }

  return {
    editingAddress,
    addingAddress,
    newCep,
    setNewCep,
    sortedAddresses,
    localDefaultAddressId,
    localSelectedAddressId,
    addressesCountLabel,
    addressFormModalKey,
    exitAddressEditor,
    handleFormSyncLocalAddresses,
    handleCardSyncLocalAddresses,
    handleNewCepSubmit,
    startAddingAddress,
    setEditingAddress,
    handleSelectAddress,
    handleMakeDefaultAddress,
  }
}
